/**
 * Inicializa o Firebase App e expõe getAuth e getFirestoreDb.
 * Firestore é obtido uma vez via getFirestoreDb() e injetado nos remotes (clean architecture).
 *
 * Firebase App Check (reCAPTCHA v3): defina `VITE_RECAPTCHA_SITE_KEY` no `.env`.
 * Em dev com enforce no backend, use `VITE_APPCHECK_DEBUG_TOKEN` (token do console).
 */

import { initializeApp, getApps, type FirebaseApp } from 'firebase/app';
import { initializeAppCheck, ReCaptchaV3Provider } from 'firebase/app-check';
import { getAuth, type Auth } from 'firebase/auth';
import { connectFunctionsEmulator, getFunctions, type Functions } from 'firebase/functions';
import { getFirestore, type Firestore } from 'firebase/firestore';

import { FIREBASE_FUNCTIONS_REGION } from '@/core/config/firebase-functions';

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

let app: FirebaseApp | null = null;

let appCheckInitialized = false;

function ensureFirebaseAppCheck(firebaseApp: FirebaseApp): void {
  if (appCheckInitialized) return;
  if (typeof window === 'undefined') return;

  const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY?.trim();
  if (!siteKey) return;

  const debugToken = import.meta.env.VITE_APPCHECK_DEBUG_TOKEN?.trim();
  if (import.meta.env.DEV && debugToken) {
    (globalThis as unknown as { FIREBASE_APPCHECK_DEBUG_TOKEN?: string }).FIREBASE_APPCHECK_DEBUG_TOKEN =
      debugToken;
  }

  initializeAppCheck(firebaseApp, {
    provider: new ReCaptchaV3Provider(siteKey),
    isTokenAutoRefreshEnabled: true,
  });
  appCheckInitialized = true;
}

export function getFirebaseApp(): FirebaseApp {
  if (getApps().length > 0) {
    const existing = getApps()[0] as FirebaseApp;
    ensureFirebaseAppCheck(existing);
    return existing;
  }
  app = initializeApp(firebaseConfig);
  ensureFirebaseAppCheck(app);
  return app;
}

export function getFirebaseAuth(): Auth {
  return getAuth(getFirebaseApp());
}

let firestoreInstance: Firestore | null = null;

/** Instância única do Firestore. Use esta função para obter o db e injetar nos remotes. */
export function getFirestoreDb(): Firestore {
  if (!firestoreInstance) {
    firestoreInstance = getFirestore(getFirebaseApp());
  }
  return firestoreInstance;
}

let functionsInstance: Functions | null = null;

/**
 * Instância única do SDK Functions na região do backend.
 * Com `VITE_USE_FUNCTIONS_EMULATOR=true`, conecta ao emulador local.
 */
export function getFirebaseFunctions(): Functions {
  if (!functionsInstance) {
    functionsInstance = getFunctions(getFirebaseApp(), FIREBASE_FUNCTIONS_REGION);
    if (import.meta.env.VITE_USE_FUNCTIONS_EMULATOR === 'true') {
      const host = import.meta.env.VITE_FUNCTIONS_EMULATOR_HOST ?? '127.0.0.1';
      const port = Number(import.meta.env.VITE_FUNCTIONS_EMULATOR_PORT ?? 5001);
      connectFunctionsEmulator(functionsInstance, host, port);
    }
  }
  return functionsInstance;
}

